// @flow
import * as React from 'react';
import { translate } from 'react-i18next';
import styled, { withTheme } from 'styled-components';
import Popover from '@material-ui/core/Popover';
import { connect } from 'react-redux';
import moment from 'moment';
import DateRange from 'common/domain/DateRange';
import { Spacer } from 'statsradio/themes/v3/Stylesheet';
import { PrimaryButton, SecondaryButton } from 'statsradio/components/ui/forms/ButtonInput';
import DateRangePopoverHeader, { COMPARISON_DATE_ID, REFERENCE_DATE_ID } from './DateRangePopoverHeader';
import DateRangePeriodSelector from './DateRangePeriodSelector';
import MetricsDateRangePicker from './MetricsDateRangePicker';
import { getCalendarMinDate } from '../../selectors/metricsSelectors';
import { getPeriod, getPreviousPeriod, yesterday, type DateRangePeriod } from '../../../domain/MetricsPeriods';

export const POPOVER_CONTAINER_ID = 'anchorEl';
export const PERIOD_SELECTOR_ID = 'datepicker-period-selector';
export const POPOVER_HEADER_CLOSED_ID = 'closed-header';
export const POPOVER_HEADER_OPENED_ID = 'opened-header';
export const REFERENCE_DATEPICKER_FACTORY_ID = 'reference-datepicker';
export const COMPARED_DATEPICKER_FACTORY_ID = 'compared-datepicker';
export const CLOSE_BUTTON_ID = 'close-button';
export const CANCEL_BUTTON_ID = 'cancel-button';
export const OK_BUTTON_ID = 'ok-button';

const PopoverContainer = styled.div`
  overflow: visible !important;
`;

const PopoverContent = styled.div`
  padding-top: ${props => props.theme.V3.metrics.margin.tiny}px;
  padding-left: ${props => props.theme.V3.metrics.margin.small}px;
  padding-right: ${props => props.theme.V3.metrics.margin.small}px;
  padding-bottom: ${props => props.theme.V3.metrics.margin.small}px;
`;

const PopoverPaper = styled.div`
  border: solid 1px;
  border-color: ${props => props.theme.V3.colors.form.border};
  border-radius: ${props => props.theme.V3.metrics.form.border.radius}px;
  background-color: ${props => props.theme.V3.colors.surface};
`;

const DateRangePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

const SSecondaryButton = styled(SecondaryButton)`
  width: 102px;
  margin-right: 20px;
`;

const SPrimaryButton = styled(PrimaryButton)`
  width: 102px;
`;

const DateRangePopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
`;

interface Props {
  defaultDateRange: DateRange;
  defaultDateRangeCompared: DateRange | void;
  defaultPeriod: DateRangePeriod;
  minDate: moment;
  onDateRangesChange: (DateRange, DateRange | void) => void;
  onPeriodChange: (period: DateRangePeriod) => void;
  theme: any;
  t: any;
}

interface State {
  anchorEl: any | void;
  comparing: boolean;
  isReferenceDateFocused: boolean;
  isComparisonDateFocused: boolean;
  dateRange: DateRange;
  dateRangeCompared: DateRange | void;
  period: DateRangePeriod;
}

export class DateRangePopover extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      anchorEl: undefined,
      comparing: false,
      isReferenceDateFocused: false,
      isComparisonDateFocused: false,
      dateRange: this.props.defaultDateRange,
      dateRangeCompared: this.props.defaultDateRangeCompared,
      period: this.props.defaultPeriod,
    };
  }

  getDateRangeCompared = () => {
    return this.props.defaultDateRangeCompared || getPreviousPeriod(this.state.period, yesterday(), this.props.minDate, this.state.dateRange);
  };

  onCompare = () => {
    const dateRangeCompared = this.getDateRangeCompared();
    this.props.onDateRangesChange(this.props.defaultDateRange, dateRangeCompared);
    this.setState({ comparing: true, isReferenceDateFocused: false, isComparisonDateFocused: true, dateRangeCompared });
  };

  onCancelCompare = () => {
    this.props.onDateRangesChange(this.props.defaultDateRange, undefined);
    this.setState({ comparing: false, isReferenceDateFocused: true, isComparisonDateFocused: false, dateRangeCompared: undefined });
  };

  openCalendar = (dateRangeInputId: string) => this.setState({ anchorEl: document.getElementById(POPOVER_CONTAINER_ID) }, () => this.focusInput(dateRangeInputId));

  focusInput = (dateRangeInputId: string) => this.setState({
    isReferenceDateFocused: dateRangeInputId === REFERENCE_DATE_ID,
    isComparisonDateFocused: dateRangeInputId === COMPARISON_DATE_ID,
  });

  closeCalendar = () => {
    this.setState({
      anchorEl: undefined,
      isReferenceDateFocused: false,
      isComparisonDateFocused: false,
    });
  };

  resetToDefaultValues = () => {
    this.setState({
      dateRange: this.props.defaultDateRange,
      dateRangeCompared: this.getDateRangeCompared(),
      period: this.props.defaultPeriod,
    });
  };

  onSelectorOpen = () => this.setState({ isComparisonDateFocused: false });

  onSelectorChange = (period: DateRangePeriod) => this.setState({
    period,
    isReferenceDateFocused: !this.state.comparing,
    dateRange: getPeriod(period, yesterday(), this.props.minDate, this.state.dateRange),
    dateRangeCompared: getPeriod(period, yesterday(), this.props.minDate, this.state.dateRangeCompared),
  });

  onDateRangeChange = (dateRange: DateRange | void) => this.setState({
    dateRange: dateRange || this.props.defaultDateRange,
    isComparisonDateFocused: false,
    isReferenceDateFocused: true,
  });

  onDateRangeComparedChange = (dateRangeCompared: DateRange | void) => this.setState({
    dateRangeCompared: dateRangeCompared || this.getDateRangeCompared(),
    isComparisonDateFocused: true,
    isReferenceDateFocused: false,
  });

  onConfirmDateRangeChanges = () => {
    this.props.onDateRangesChange(this.state.dateRange, this.state.dateRangeCompared);
    this.props.onPeriodChange(this.state.period);
    this.closeCalendar();
  };

  onCancelDateRangeChanges = () => {
    this.resetToDefaultValues();
    this.closeCalendar();
  };

  render() {
    const { minDate, defaultDateRange, theme, t } = this.props;
    const { anchorEl, comparing, isReferenceDateFocused, isComparisonDateFocused, period, dateRange, dateRangeCompared } = this.state;
    const showCalendar = Boolean(anchorEl);
    const dateRangeComparedChanged = dateRangeCompared ? !this.getDateRangeCompared().equals(dateRangeCompared) : false;
    const dateRangesChanged = !defaultDateRange.equals(dateRange) || dateRangeComparedChanged;

    return (
      <React.Fragment>
        <PopoverContainer id={POPOVER_CONTAINER_ID}>
          <DateRangePopoverHeader
            id={POPOVER_HEADER_CLOSED_ID}
            dateRange={dateRange}
            dateRangeCompared={dateRangeCompared}
            onDateRangeClick={this.openCalendar}
            onCompareClick={this.onCompare}
            onCancelCompareClick={this.onCancelCompare}
            comparing={comparing}
          />
        </PopoverContainer>

        <Popover
          open={showCalendar}
          anchorEl={anchorEl}
          transformOrigin={{
            vertical: theme.V3.metrics.margin.tiny,
            horizontal: theme.V3.metrics.margin.small,
          }}
          onClose={this.onCancelDateRangeChanges}
          elevation={0}
          PaperProps={{ square: true, component: PopoverPaper }}
          container={() => {
            return document.getElementById(POPOVER_CONTAINER_ID);
          }}
        >
          <PopoverContent>
            <DateRangePopoverHeader
              id={POPOVER_HEADER_OPENED_ID}
              dateRange={dateRange}
              dateRangeCompared={dateRangeCompared}
              onDateRangeClick={this.focusInput}
              onCompareClick={this.onCompare}
              onCancelCompareClick={this.onCancelCompare}
              comparing={comparing}
              referenceDateOnFocus={isReferenceDateFocused}
              comparisonDateOnFocus={isComparisonDateFocused}
            />

            <DateRangePopoverContent>
              <DateRangePeriodSelector
                id={PERIOD_SELECTOR_ID}
                defaultValue={period}
                onChange={this.onSelectorChange}
                onOpen={this.onSelectorOpen}
              />
              <Spacer horizontal />
              <DateRangePickerContainer>
                <MetricsDateRangePicker
                  id={REFERENCE_DATEPICKER_FACTORY_ID}
                  dateRange={dateRange}
                  maxDate={yesterday()}
                  minDate={minDate}
                  onChange={this.onDateRangeChange}
                  period={period}
                />
                {comparing &&
                  <MetricsDateRangePicker
                    id={COMPARED_DATEPICKER_FACTORY_ID}
                    dateRange={dateRangeCompared || this.getDateRangeCompared()}
                    maxDate={yesterday()}
                    minDate={minDate}
                    onChange={this.onDateRangeComparedChange}
                    period={period}
                  />
                }
              </DateRangePickerContainer>

              { dateRangesChanged ?
                <ButtonContainer>
                  <SSecondaryButton
                    id={CANCEL_BUTTON_ID}
                    onClick={this.onCancelDateRangeChanges}
                    type="reset"
                  >
                    {t('cancel')}
                  </SSecondaryButton>
                  <SPrimaryButton
                    id={OK_BUTTON_ID}
                    value={t('ok')}
                    onClick={this.onConfirmDateRangeChanges}
                    type="submit"
                  />
                </ButtonContainer>
                :
                <SPrimaryButton
                  id={CLOSE_BUTTON_ID}
                  value={t('close')}
                  onClick={this.onCancelDateRangeChanges}
                  type="reset"
                />
              }
            </DateRangePopoverContent>
          </PopoverContent>

        </Popover>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    minDate: getCalendarMinDate(state),
  };
}
export default translate()(connect(mapStateToProps)(withTheme(DateRangePopover)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/metrics/v2/components/parameters/DateRangePopover.js